import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";

import { SectionHeader } from "@tvg/design-system";
import { RaceCell } from "@tvg/design-system/web/components/RaceCell";
import { Race } from "@tvg/ts-types/Race";
import buildRaceUrl from "@tvg/formatter/url";
import { useUpcomingRaces } from "@urp/lib-racetracks";
import { Module } from "@tvg/amplitude/modules/raceCell/types";
import { sendRaceCellClickedAnalyticEvt } from "@urp/lib-racetracks/src/utils/analytics";
import {
  hasPromoAvailable,
  isUserOptedInPromo,
  getTrackName
} from "@urp/lib-racetracks/src/utils/races";
import { getActiveBets } from "@urp/lib-racetracks/src/redux/selectors";
import { EmptyMessage } from "@urp/tracks-builder";
import { isFilterApplied } from "@urp/tracks-builder/src/utils";
import {
  getIsLogged,
  getUpcomingRacesInfo,
  getOptedInPromos
} from "@urp/store-selectors";
import { Container, Element, ListContainer } from "./styled-components";
import UpcomingRacesMask from "./UpcomingRacesMask";
import { UpcomingRacesProps } from "./types";
import NoUpcomingRaces from "./NoUpcomingRaces";

const UpcomingRaces = ({
  qaLabel = "upcoming-races",
  filterOptions,
  title,
  noUpcomingRacesMessages
}: UpcomingRacesProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isUserLogged = useSelector(getIsLogged);
  const optedInPromos = useSelector(getOptedInPromos);
  const { maxResults } = useSelector(getUpcomingRacesInfo);

  const { upcomingRaces, loading } = useUpcomingRaces({
    filterOptions,
    maxResults
  });
  const activeBets = useSelector(getActiveBets);

  useEffect(() => {
    const element = ref.current;
    setTimeout(() =>
      element?.scroll({
        left: 0,
        behavior: "smooth"
      })
    );
  }, [
    filterOptions?.raceTypesFilter?.length,
    filterOptions?.regionsFilter?.length,
    filterOptions?.tracksSearchQuery
  ]);

  if (loading) {
    return <UpcomingRacesMask />;
  }

  if (!upcomingRaces?.length) {
    if (!noUpcomingRacesMessages) return null;

    if (isFilterApplied(filterOptions)) {
      return <EmptyMessage />;
    }
    return (
      <NoUpcomingRaces
        title={noUpcomingRacesMessages.title}
        hint={noUpcomingRacesMessages.hint}
      />
    );
  }

  return (
    <Container data-qa-label={qaLabel}>
      {title && <SectionHeader title={title} />}
      <ListContainer ref={ref} data-qa-label={`${qaLabel}-list`}>
        {upcomingRaces?.map((race: Race) => {
          const availableRacePromos = hasPromoAvailable(race);
          const isOptedIn =
            isUserLogged &&
            isUserOptedInPromo(optedInPromos, availableRacePromos);
          const raceUrl = buildRaceUrl(
            race.track.code,
            race.track.name,
            +race.number
          );
          return (
            <Element key={race.id}>
              <RaceCell.Details
                race={race}
                to={raceUrl}
                numberActiveBets={get(activeBets, race?.tvgRaceId ?? "", 0)}
                qaLabel={`${qaLabel}-${race.id}`}
                hasPromosAvailable={
                  (!isUserLogged || !isOptedIn) && !!availableRacePromos.length
                }
                onClick={() =>
                  sendRaceCellClickedAnalyticEvt({
                    module: Module.UPCOMING_RACES,
                    trackName: getTrackName(race.track.name),
                    raceNumber: race.number,
                    linkUrl: raceUrl,
                    promoOptedIn: isOptedIn ? "true" : "none",
                    trackHasPromos: !!race.track.hasAboveTheLinePromo,
                    raceHasPromos: !!availableRacePromos.length,
                    isFDTV: race.video.onTvg || race.video.onTvg2,
                    trackCountry: race.track.location?.country || ""
                  })
                }
              />
            </Element>
          );
        })}
      </ListContainer>
    </Container>
  );
};

export default UpcomingRaces;
